import {
  Row,
  useColumnOrder,
  useFilters,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table'
import { useConditionalSelectColumn } from 'core/table/table-hooks/useSelectColumn'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useChampionColumns, { useStyles } from 'champion/hooks/useChampionColumns'
import { useVolunteersCollection } from 'volunteering/volunteers/hooks'
import { useDateRangeFilter } from 'filtering/hooks'
import Grid from '@material-ui/core/Grid'
import { useNationalAvgVolunteerValuePerHour } from 'champion/hooks/useNationalAvgVolunteerValuePerHour'
import MessageOutlinedIcon from '@material-ui/icons/MessageOutlined'
import RemoveCircleOutlineOutlinedIcon from '@material-ui/icons/RemoveCircleOutlineOutlined'
import useMessageRecipientsPrompt from 'messages/hooks/useMessageRecipientsPrompt'
import Loading from 'components/Loading'
import { useFiltersFromQuery } from 'core/table/components/NewFiltersModal'
import { RouteComponentProps } from 'react-router'
import { buildTableFilters, useGetGlobalFilter } from 'core/table/filters'
import { TableMenuButton } from 'civic-champs-shared/core/TableMenuButton'
import ExportChampionsButton from 'champion/components/ExportChampionsButton'
import { useHasRole } from 'auth/hooks'
import { INTERNAL_ADMIN, SUPER_ADMIN } from 'civic-champs-shared/auth/utils/permissions'
import { ExtendedPagedTable } from 'core/table/components/ExtendedPagedTable'
import AddIcon from '@material-ui/icons/Add'
import ContainedButton from 'civic-champs-shared/core/ContainedButton'
import useGetColumnState from 'core/table/table-hooks/useGetColumnState'
import TablePopoverButton from 'civic-champs-shared/core/TablePopoverButton'
import { useShowPrompt } from 'civic-champs-shared/core/modal/hooks'
import { SendNotificationModal } from 'champion/components/SendNotificationModal'
import useSendNotification from 'champion/hooks/useSendNotification'
import first from 'lodash/first'
import { useCurrentOrg, useTimeZone } from 'civic-champs-shared/auth/hooks'
import CUSTOM_FILTERS from 'champion/utils/filters'
import { useFeatureEnabled } from 'core/feature/hooks'
import PageHeader from 'App/components/PageHeader'
import { useGetSearchableContactsFromVolunteersArray } from 'messages/hooks/useGetSearchableContactsFromVolunteersArray'
import { ReactComponent as VerifiedIcon } from 'images/verified.svg'
import { useHandleChat } from 'chats'
import useGetChampionSummary from 'champion/utils/useGetChampionsSummary'
import { Volunteer } from 'champion/utils/interface'
import LabelOutlinedIcon from '@material-ui/icons/LabelOutlined'
import MailOutlineIcon from '@material-ui/icons/MailOutline'
import { useAddRemoveTagsPrompt } from '../hooks/useAddRemoveTagsPrompt'
import { Person } from '../../people/interface'
import GroupOutlinedIcon from '@material-ui/icons/GroupOutlined'
import useMultiGroupAddMembersPrompt from 'group/hooks/useMultiGroupAddMembersPrompt'
import usePushChampions from '../hooks/usePushChampions'
import { IntegrationKey, IntegrationProvider } from '../../integrations/types'
import useGetIntegrationKey, { emptyValue } from '../../integrations/hooks/useGetIntegrationKey'
import SaveAltOutlinedIcon from '@material-ui/icons/SaveAltOutlined'
import ImportVolunteersPrompt from '../components/ImportVolunteersPrompt'
import { ExportChampionsMenu } from '../components/ExportChampionsMenu'
import { ChampionsServerSide } from 'champion/pages/ChampionsServerSide'
import { championsHiddenColumns, championsTableName } from 'champion/championsTableConfig'
import { useAdminI18n } from 'i18n/feature'
import { useChampionsPageI18n } from 'champion/hooks/useChampionsPageI18n'

export const Champions = (props: RouteComponentProps) => {
  const isChampionsServerSideListEnabled = useFeatureEnabled('ChampionsServerSideList')
  const { t } = useAdminI18n('admin')

  return (
    <PageHeader
      title={t('pages.champions.title', { defaultValue: 'Champions' })}
      subTitle={t('pages.champions.subtitle', {
        defaultValue: "Add and manage your organization's volunteers",
      })}
    >
      {isChampionsServerSideListEnabled ? <ChampionsServerSide {...props} /> : <ChampionsClient {...props} />}
    </PageHeader>
  )
}

const ChampionsClient = ({ location, history }: RouteComponentProps) => {
  const skipReset = useRef<boolean>()
  const isSuperAdmin = useHasRole(SUPER_ADMIN)
  const isInternalAdmin = useHasRole(INTERNAL_ADMIN)
  const [dateRange] = useDateRangeFilter()
  const showMessageRecipientsPrompt = useMessageRecipientsPrompt()
  const valuePerHour = useNationalAvgVolunteerValuePerHour()
  const { labels, cards, getOperatorOptions } = useChampionsPageI18n(valuePerHour)
  const { filters } = useFiltersFromQuery(location.search, getOperatorOptions)
  const [includeInactive, setIncludeInactive] = useState(true)
  const [
    { volunteers, initiallyLoaded, loading, loadingProgress },
    { addVolunteerLegacy: addVolunteer, removeVolunteer, handleTagsChange, handleGroupsAdded },
    eventListeners,
  ] = useVolunteersCollection(dateRange, includeInactive) as any
  const columns = useChampionColumns()
  const columnStyles = useStyles()
  const getSearchableContacts = useGetSearchableContactsFromVolunteersArray(volunteers)
  const [fetchColumnState, { loading: columnStateLoading, result: columnState }] = useGetColumnState()
  const showNotificationsModal = useShowPrompt(SendNotificationModal)
  const organization = useCurrentOrg()
  const timeZone = useTimeZone()
  const filterTypes = useMemo(() => ({ ...buildTableFilters(timeZone), ...CUSTOM_FILTERS }), [timeZone])
  const championsTagsEnabled = useFeatureEnabled('ChampionsTags')
  const chatsEnabled = useFeatureEnabled('Chats')
  const getSummary = useGetChampionSummary(valuePerHour)
  const showTagsPrompt = useAddRemoveTagsPrompt(handleTagsChange)
  const isLglIntegrationEnabled = useFeatureEnabled('LglIntegration')
  const isFastnIntegrationEnabled = useFeatureEnabled('FastnIntegration')
  const isDonorPerfectIntegrationEnabled = useFeatureEnabled('DonorPerfectIntegration')
  const lglChampionsSyncEnabled = useFeatureEnabled('ChampionsLGLSync')
  const bulkAddMembersEnabled = useFeatureEnabled('BulkAddMembers')
  const newVolunteerImportEnabled = useFeatureEnabled('NewVolunteerImport')
  const newVolunteerImportForSuperAdminEnabled = useFeatureEnabled('NewVolunteerImportForSuperAdmin')
  const showImportButton =
    newVolunteerImportEnabled && (isInternalAdmin || (newVolunteerImportForSuperAdminEnabled && isSuperAdmin))
  const pushChampions = usePushChampions()
  const showImportVolunteerPrompt = useShowPrompt(ImportVolunteersPrompt)

  const [integrationKey, setIntegrationKey] = useState<IntegrationKey>(emptyValue)
  const [fastnIntegrationKey, setFastnIntegrationKey] = useState<IntegrationKey>(emptyValue)
  const [donorPerfectIntegrationKey, setDonorPerfectIntegrationKey] = useState<IntegrationKey>(emptyValue)
  const fetchIntegrationKey = useGetIntegrationKey()
  useEffect(() => {
    fetchIntegrationKey(IntegrationProvider.LGL).then(setIntegrationKey)
    fetchIntegrationKey(IntegrationProvider.FASTN).then(setFastnIntegrationKey)
    fetchIntegrationKey(IntegrationProvider.DONOR_PERFECT).then(setDonorPerfectIntegrationKey)
  }, [fetchIntegrationKey])

  const globalFilter = useGetGlobalFilter({
    columns,
    filterTypes,
    getOperatorOptions,
  })
  const [sendNotification, { loading: sendNotificationLoading }] = useSendNotification(organization.id)
  const handleResendVerification = useCallback(
    (rows: any[]) => {
      const data = rows.map(({ original }) => original)
      const {
        user: { id, email, phoneNumber },
      } = first(data)
      if (rows.length > 1 || (!email && !phoneNumber)) {
        showNotificationsModal({ persons: data })
      } else {
        sendNotification([{ personId: id, sendMethod: email ? 'email' : 'phone' }])
      }
    },
    [sendNotification, showNotificationsModal],
  )

  useEffect(() => {
    fetchColumnState(championsTableName)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const table = useTable(
    {
      initialState: {
        hiddenColumns: championsHiddenColumns,
        // @ts-ignore
        globalFilter: '',
        filters,
      },
      globalFilter,
      // @ts-ignore
      filterTypes,
      data: volunteers,
      columns,
      autoResetSortBy: !skipReset.current,
      autoResetPage: !skipReset.current,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    useColumnOrder,
    useConditionalSelectColumn(() => true, columnStyles.selectColumn),
  )

  const [selectedRows, selectedRowsCount, selectedUnverifiedRows, selectedUnverifiedRowsCount] = useMemo(() => {
    const selectedUnverifiedRows = table.selectedFlatRows.filter(
      ({
        original: {
          user: { emailVerified, phoneNumberVerified },
        },
      }: any) => !emailVerified && !phoneNumberVerified,
    )
    return [
      table.selectedFlatRows,
      table.selectedFlatRows.length,
      selectedUnverifiedRows,
      selectedUnverifiedRows.length,
    ]
  }, [table.selectedFlatRows])

  const handlePushChampions = useCallback(async () => {
    await pushChampions({
      provider: IntegrationProvider.LGL,
      personIds: selectedRows.map((row: Row<any>) => row.original.user.id),
    })
  }, [pushChampions, selectedRows])

  const handlePushChampionsToFastn = useCallback(async () => {
    await pushChampions({
      provider: IntegrationProvider.FASTN,
      personIds: selectedRows.map((row: Row<any>) => row.original.user.id),
    })
  }, [pushChampions, selectedRows])

  const handlePushChampionsToDonorPerfect = useCallback(async () => {
    await pushChampions({
      provider: IntegrationProvider.DONOR_PERFECT,
      personIds: selectedRows.map((row: Row<any>) => row.original.user.id),
    })
  }, [pushChampions, selectedRows])

  const onMessageVolunteers = () => {
    const recipientPersonIds = selectedRows.map((row: any) => row.original.user.id)
    showMessageRecipientsPrompt({
      recipientPersonIds,
      getSearchableContacts,
      autocompleteSectionTitle: labels.allVolunteers,
    })
  }

  const onRemoveVolunteer = async () => {
    await removeVolunteer(selectedRows.map((row: any) => row.original))
    table.toggleAllRowsSelected(false)
  }

  const onUpdateTags = useCallback(() => {
    showTagsPrompt(selectedRows.map((row: any) => row.original.user as Person))
  }, [selectedRows, showTagsPrompt])

  const [handleChat, processing] = useHandleChat({ selectedRows, history })

  const showAddMembersPrompt = useMultiGroupAddMembersPrompt(handleGroupsAdded)
  const onBulkAddMembers = useCallback(async () => {
    const selectedPersons = selectedRows.map((row: any) => row.original.user as Person)
    await showAddMembersPrompt(selectedPersons)
  }, [selectedRows, showAddMembersPrompt])

  const booleanFilters = useMemo(
    () => [{ name: labels.showAllChampions, value: includeInactive, onChange: setIncludeInactive }],
    [includeInactive, labels.showAllChampions],
  )

  if (!initiallyLoaded || columnStateLoading || processing || loading) {
    return <Loading progress={loadingProgress} animateProgress />
  }

  return (
    <Grid container>
      <ExtendedPagedTable<Volunteer>
        getSummary={getSummary}
        cards={cards}
        history={history}
        location={location}
        table={table}
        columns={columns}
        getOperatorOptions={getOperatorOptions}
        binaryFilters={booleanFilters}
        columnState={columnState}
        tableName={championsTableName}
        searchPlaceholder={labels.searchPlaceholder}
        filterSubHeader={labels.filterSubHeader}
        buttons={
          <>
            <TableMenuButton
              startIcon={<MailOutlineIcon />}
              onClick={onMessageVolunteers}
              disabled={!selectedRowsCount}
              rightMargin
            >
              {labels.message}
            </TableMenuButton>
            <ExportChampionsButton
              data={selectedRows}
              disabled={!selectedRowsCount}
              includeDonations={isSuperAdmin}
              exportLabel={labels.export}
            />
          </>
        }
        popover={
          <>
            <TablePopoverButton
              startIcon={<VerifiedIcon style={{ width: '20px', height: '20px' }} />}
              disabled={!selectedUnverifiedRowsCount || sendNotificationLoading}
              onClick={() => handleResendVerification(selectedUnverifiedRows)}
            >
              {labels.resendVerification}
            </TablePopoverButton>

            {chatsEnabled && (
              <TablePopoverButton startIcon={<MessageOutlinedIcon />} onClick={handleChat}>
                {labels.addToChat}
              </TablePopoverButton>
            )}
            {bulkAddMembersEnabled && (
              <TablePopoverButton startIcon={<GroupOutlinedIcon />} onClick={onBulkAddMembers}>
                {labels.addToGroup}
              </TablePopoverButton>
            )}
            {championsTagsEnabled && (
              <TablePopoverButton startIcon={<LabelOutlinedIcon />} onClick={onUpdateTags}>
                {labels.addRemoveTags}
              </TablePopoverButton>
            )}
            <ExportChampionsMenu
              disabled={!selectedRowsCount}
              exportToLabel={labels.exportTo}
              options={[
                {
                  label: labels.littleGreenLight,
                  onClick: handlePushChampions,
                  enabled:
                    isLglIntegrationEnabled &&
                    lglChampionsSyncEnabled &&
                    integrationKey.enabled &&
                    !!integrationKey.token,
                },
                {
                  label: labels.donorPerfect,
                  onClick: handlePushChampionsToDonorPerfect,
                  enabled:
                    isDonorPerfectIntegrationEnabled &&
                    donorPerfectIntegrationKey.enabled &&
                    !!donorPerfectIntegrationKey.token,
                },
                {
                  label: labels.fastn,
                  onClick: handlePushChampionsToFastn,
                  enabled: isFastnIntegrationEnabled && fastnIntegrationKey.enabled && !!fastnIntegrationKey.token,
                },
              ]}
            />
            <TablePopoverButton
              startIcon={<RemoveCircleOutlineOutlinedIcon />}
              disabled={!selectedRowsCount}
              onClick={onRemoveVolunteer}
            >
              {selectedRowsCount > 1 ? labels.removeChampions : labels.removeChampion}
            </TablePopoverButton>
            {showImportButton && (
              <TablePopoverButton startIcon={<SaveAltOutlinedIcon />} onClick={showImportVolunteerPrompt}>
                {labels.importVolunteers}
              </TablePopoverButton>
            )}
          </>
        }
        addButton={
          <ContainedButton startIcon={<AddIcon />} onClick={addVolunteer}>
            {labels.addChampion}
          </ContainedButton>
        }
        useGlobalSearch
        useFilters
        useDateRange
        eventListeners={eventListeners}
        skipReset={skipReset}
      />
    </Grid>
  )
}
